<script lang="ts">
  import { type Business } from '../../lib/types';

  let {
    business,
    isDefault,
    ownerEmail,
    onedit,
    onsetdefault,
    ontoggleactive,
    onarchive,
  }: {
    business: Business;
    isDefault: boolean;
    ownerEmail: string;
    onedit: () => void;
    onsetdefault: () => void;
    ontoggleactive: () => void;
    onarchive: () => void;
  } = $props();
</script>

<div class="business-card" class:inactive={business.isActive === false}>
  <div class="business-header">
    <div class="business-avatar">
      {business.name.charAt(0).toUpperCase()}
    </div>
    <div class="business-info">
      <div class="business-name-row">
        <h3>{business.name}</h3>
        <span class="business-status-badge" class:active={business.isActive !== false}>
          {business.isActive !== false ? 'Active' : 'Inactive'}
        </span>
        {#if isDefault}
          <span class="business-default-badge">Default</span>
        {/if}
      </div>
      {#if business.industry}
        <span class="business-industry">{business.industry}</span>
      {/if}
    </div>
    <div class="business-actions">
      {#if !isDefault}
        <button
          class="btn-set-default"
          title="Set as default business for EQUOS app"
          onclick={onsetdefault}
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          Set Default
        </button>
      {/if}
      <button
        class="btn-icon"
        title={business.isActive !== false ? 'Deactivate business' : 'Activate business'}
        onclick={ontoggleactive}
      >
        <svg viewBox="0 0 20 20" fill="currentColor">
          {#if business.isActive !== false}
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clip-rule="evenodd" />
          {:else}
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
          {/if}
        </svg>
      </button>
      <button class="btn-icon" title="Edit business" onclick={onedit}>
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
      </button>
      <button class="btn-icon btn-danger" title="Archive business" onclick={onarchive}>
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
          <path fill-rule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </div>

  <div class="business-meta">
    <span class="meta-item">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
      </svg>
      Owner: {ownerEmail}
    </span>
    <span class="meta-item">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
      </svg>
      Created: {business.createdAt instanceof Date ? business.createdAt.toLocaleDateString() : new Date(business.createdAt).toLocaleDateString()}
    </span>
  </div>
</div>

<style>
  .business-card {
    background: var(--color-gray-50);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-md);
    padding: var(--space-5);
  }

  .business-card.inactive {
    opacity: 0.7;
    border-color: var(--color-error-border);
    background: #fef2f2;
  }

  .business-card.inactive .business-avatar {
    background: var(--color-gray-400);
  }

  .business-header {
    display: flex;
    align-items: center;
    gap: var(--space-4);
  }

  .business-avatar {
    width: 48px;
    height: 48px;
    border-radius: var(--radius-xl);
    background: var(--gradient-primary);
    color: var(--color-white);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    font-weight: 600;
    flex-shrink: 0;
  }

  .business-info {
    flex: 1;
  }

  .business-info h3 {
    margin: 0;
    font-size: 1.1rem;
    color: var(--color-gray-800);
  }

  .business-name-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .business-status-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: var(--color-error-bg);
    color: var(--color-error-text);
  }

  .business-status-badge.active {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  .business-default-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }

  .business-industry {
    font-size: 0.85rem;
    color: var(--color-gray-500);
  }

  .business-actions {
    display: flex;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .btn-set-default {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #667eea;
    background: white;
    color: #667eea;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-set-default:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
  }

  .btn-set-default svg {
    width: 16px;
    height: 16px;
  }

  .business-meta {
    display: flex;
    gap: var(--space-6);
    margin-top: var(--space-4);
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-gray-200);
    flex-wrap: wrap;
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 0.85rem;
    color: var(--color-gray-500);
  }

  .meta-item svg {
    width: 16px;
    height: 16px;
    color: var(--color-gray-400);
  }
</style>
